import { motion } from "framer-motion";
import { CheckCircle } from "@phosphor-icons/react";
import { fadeUpInView } from "./animationVariants";
import { BRAND } from "@/lib/seo";

const LAST_REVIEWED = "June 2026";

function Block({ children, className = "" }) {
  return (
    <motion.div {...fadeUpInView} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * ProgramDepth — deep-content sections appended to every program page:
 * who it's for, biomarkers, journey timeline, evidence links, reviewer strip,
 * crawlable FAQ (<details>), and the standardized primary CTA.
 */
export default function ProgramDepth({ program, depth, isMedical }) {
  if (!depth) return null;

  return (
    <>
      {/* Who it's for */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-20 md:mt-28">
        <Block>
          <div className="eyebrow mb-3">Who It&rsquo;s For</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
            Is this <em className="not-italic text-primary">your</em> program?
          </h2>
        </Block>
        <div data-testid="program-who-for" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {depth.whoFor.map((item) => (
            <div key={item} className="flex items-start gap-3 border border-border bg-card p-4">
              <CheckCircle size={20} weight="duotone" className="text-accent flex-shrink-0 mt-0.5" />
              <p className="text-base text-foreground/80 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What we measure */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <Block>
          <div className="eyebrow mb-5">What We Measure</div>
          <div data-testid="program-measures" className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {depth.measures.map((m) => (
              <div
                key={m}
                className="border border-border bg-card py-3 px-3 text-center font-mono text-[0.62rem] md:text-[0.68rem] uppercase tracking-[0.1em] text-primary font-semibold"
              >
                {m}
              </div>
            ))}
          </div>
        </Block>
      </section>

      {/* How it works — journey */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <Block>
          <div className="eyebrow mb-3">How It Works</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
            Your <em className="not-italic text-primary">journey</em>.
          </h2>
        </Block>
        <div data-testid="program-timeline" className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-3">
          {depth.timeline.map((t) => (
            <div key={t.step} className="border border-border bg-card p-5 flex flex-col">
              <span className="font-mono text-[0.62rem] tracking-[0.24em] text-accent font-bold">
                {t.step}
              </span>
              <h3 className="mt-3 font-display text-base font-bold text-primary leading-snug">
                {t.label}
              </h3>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Evidence + reviewer */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Block className="border border-border bg-card p-7">
            <div className="eyebrow mb-4">Scientific Evidence</div>
            <p className="text-base text-foreground/75 leading-relaxed">
              Every clinical concept behind this program is graded and cited in our
              physician-reviewed Evidence Library.
            </p>
            <div data-testid="program-evidence-links" className="mt-5 flex flex-col gap-2.5">
              {depth.evidence.map((e) => (
                <a
                  key={e.href}
                  href={e.href}
                  className="inline-flex items-center gap-2 font-mono text-[0.68rem] uppercase tracking-[0.16em] text-primary hover:text-accent font-semibold transition-colors"
                >
                  <span aria-hidden className="text-accent">§</span> {e.label} →
                </a>
              ))}
              <a
                href="/evidence"
                className="inline-flex items-center gap-2 font-mono text-[0.68rem] uppercase tracking-[0.16em] text-foreground/60 hover:text-accent transition-colors"
              >
                Browse the full Evidence Library →
              </a>
            </div>
          </Block>

          <Block
            className="border border-border bg-card p-7 font-mono text-[0.7rem] leading-relaxed tracking-wide text-foreground/70 space-y-1.5"
          >
            <div className="eyebrow mb-4">Clinical Oversight</div>
            {isMedical ? (
              <>
                <p data-testid="program-reviewer">
                  <span className="text-accent font-semibold">Medically reviewed by:</span>{" "}
                  <strong className="text-primary">{BRAND.medicalDirector.name}</strong> — Medical
                  Director, {BRAND.medicalOrg}
                </p>
                <p>
                  Delivered as licensed medical care. Labs ordered, protocols approved, and therapy
                  titrated by licensed clinicians.
                </p>
              </>
            ) : (
              <>
                <p data-testid="program-reviewer">
                  <span className="text-accent font-semibold">Reviewed by:</span>{" "}
                  <strong className="text-primary">{BRAND.name} Clinical Team</strong>
                </p>
                <p>
                  Delivered as a non-medical wellness program. If your data surfaces a clinical
                  issue, you are routed to our physician-led medical arm.
                </p>
              </>
            )}
            <p>
              <span className="text-accent font-semibold">Last reviewed:</span> {LAST_REVIEWED}
            </p>
          </Block>
        </div>
      </section>

      {/* FAQ — native <details> so answers exist in prerendered HTML */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <Block>
          <div className="eyebrow mb-3">Frequently Asked Questions</div>
          <div data-testid="program-faq" className="mt-4 divide-y divide-border border border-border bg-card">
            {depth.faqs.map((f) => (
              <details key={f.q} className="group px-6">
                <summary className="cursor-pointer list-none py-5 flex items-start justify-between gap-4">
                  <h3 className="font-display text-base md:text-lg font-semibold text-primary leading-snug m-0">
                    {f.q}
                  </h3>
                  <span aria-hidden className="text-accent font-bold text-lg leading-none mt-0.5 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="pb-6 text-base text-foreground/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </Block>
      </section>

      {/* Primary CTA */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-20">
        <Block className="border-2 border-primary/25 bg-card p-8 md:p-10 text-center">
          <div className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">
            Ready to start {program.title}?
          </div>
          <p className="mt-3 text-base md:text-lg text-foreground/70 max-w-xl mx-auto">
            One free assessment routes you to the right program — no commitment required.
          </p>
          <a
            href="/get-started"
            data-testid="program-bottom-cta"
            className="mt-7 iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.72rem]"
          >
            Start My Health Assessment <span aria-hidden>→</span>
          </a>
        </Block>
      </section>
    </>
  );
}
